import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { FiEye, FiEyeOff } from "react-icons/fi";
import { useDispatch } from "react-redux";
import { loginCompany } from "../../Redux/companySlice";
import { clearUserState } from "../../Redux/userSlice";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const LoginForm = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  useEffect(() => {
    // Check if redirected due to token expiration
    const params = new URLSearchParams(location.search);
    const expired = params.get('expired');
    if (expired === 'true') {
      toast.error('Your session has expired. Please login again.');
    }
  }, [location]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!email || !password) {
      const errorMsg = "Email and password are required";
      setError(errorMsg);
      toast.error(errorMsg);
      return;
    }

    setIsLoading(true);

    try {
      // Clear any influencer session first
      const currentUserType = localStorage.getItem('userType');
      if (currentUserType === 'influancer') {
        dispatch(clearUserState());
      }

      // Login as company using Redux thunk
      const credentials = {
        email,
        password,
      };
      const result = await dispatch(loginCompany(credentials)).unwrap();

      if (!result.company) {
        throw new Error("Invalid response format");
      }

      const companyName = result.company.companyName || "";
      const firstName = companyName.split('.')[0] || companyName || "";

      toast.success(`Welcome back, ${firstName}!`);
      
      // Always redirect to dashboard, not marketplace
      const redirectTo = "/company/dashboard";
      navigate(redirectTo, { replace: true });

    } catch (err) {
      console.error("Login error:", err);
    
      const errorMsg =
        typeof err === "string"
          ? err
          : err?.message || "Unable to login. Please try again.";
    
      setError(errorMsg);
      // toast.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center h-[calc(100dvh-80px)] bg-gray-100 px-2 md:px-4 overflow-hidden">
      <ToastContainer position="top-right" autoClose={3000} />
      {/* <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-orange-500">
        Infuber
      </h1> */}
      <div className="bg-white p-2 md:p-4 rounded-xl shadow-lg w-full max-w-sm sm:max-w-md lg:max-w-xl">
        <div className="text-center mb-6">
          <h5 className="mt-2 text-gray-700 font-bold text-xs md:text-sm">
            Welcome back, friend.
          </h5>
          <p className="text-xs md:text-sm text-gray-500">
            We're so glad to see you! Login to continue.
          </p>
        </div>

        {/* Display error message if exists */}
        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
            {error}
          </div>
        )}

        {/* Form Start */}
        <form onSubmit={handleSubmit}>
          <div className="space-y-4">


            {/* Email Input */}
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700">
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className="mt-1 w-full p-2 border rounded-lg text-sm sm:text-base focus:ring-orange-500 focus:border-orange-500"
                required
              />
            </div>
            <div className="relative">
              <label className="block text-xs sm:text-sm font-medium text-gray-700">
                Password
              </label>
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                className="mt-1 w-full p-2 pr-10 border rounded-lg text-sm sm:text-base focus:ring-orange-500 focus:border-orange-500"
                required
              />
              <span
                className="absolute inset-y-0 right-3 flex items-center cursor-pointer text-gray-600"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <FiEyeOff /> : <FiEye />}
              </span>
              <Link
                to="/company/forgot-password"
                className="text-orange-500 hover:text-orange-600 flex text-sm font-medium justify-end mt-2 mr-1"
              >
                Forgot your password?
              </Link>
            </div>
            {/* Remember Me Checkbox */}
            <div className="flex items-center space-x-2">
              <input type="checkbox" className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="text-xs sm:text-sm text-gray-700">
                Remember me
              </span>
            </div>

            {/* Login Button with Loading Spinner */}
            <button
              type="submit"
              disabled={isLoading}
              className={`w-full bg-orange-500 text-white py-2 rounded-lg hover:bg-orange-600 transition text-sm sm:text-base flex items-center justify-center font-medium ${
                isLoading ? "opacity-70 cursor-not-allowed" : ""
              }`}
            >
              {isLoading ? (
                <>
                  <svg
                    className="animate-spin h-5 w-5 mr-2 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v2a6 6 0 000 12v2a8 8 0 01-8-8z"
                    />
                  </svg>
                </>
              ) : (
                "Login"
              )}
            </button>
          </div>
        </form>

        {/* Footer Links */}
        <p className="text-xs sm:text-sm text-gray-500 text-center mt-4">
          <Link
            to="/company/signup"
            className="text-orange-500 font-semibold hover:text-orange-600"
          >
            Register
          </Link>{" "}
          •{" "}
          <Link to="/company/request-verification" className="text-gray-500 hover:text-gray-600">
            Send verification email
          </Link>
        </p>
      </div>
    </div>
  );
};

export default LoginForm;